import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MailCheck, Building2 } from "lucide-react";
import AuthShell, { primaryButtonClass, secondaryButtonClass } from "./AuthShell";

// Employee path: accounts are created by a company admin, so this page
// explains what to do depending on whether the company is registered.
const Steps = ({ items }) => (
  <ol className="space-y-3 mb-6">
    {items.map((text, i) => (
      <li key={i} className="flex gap-3 text-sm text-gray-700">
        <span className="h-6 w-6 shrink-0 flex items-center justify-center rounded-full bg-[#1C1A14] text-[#FFC81E] text-xs font-semibold">
          {i + 1}
        </span>
        <span className="pt-0.5">{text}</span>
      </li>
    ))}
  </ol>
);

const JoinCompany = () => {
  const navigate = useNavigate();
  const [answer, setAnswer] = useState(null); // null | "yes" | "no"

  if (answer === "yes") {
    return (
      <AuthShell
        title="Check your email"
        subtitle="Your admin adds you to the company."
        onBack={() => setAnswer(null)}
      >
        <div className="flex justify-center mb-5 text-[#1C1A14]">
          <MailCheck size={36} />
        </div>
        <Steps
          items={[
            "Your company admin invites you using your work email.",
            "Open the invite email and use the link to set your password.",
            "Come back here and sign in with that email and password.",
          ]}
        />
        <p className="text-sm text-gray-600 mb-6">
          No email yet? Check your spam folder, or ask your admin to send the invite again.
        </p>
        <button type="button" className={primaryButtonClass} onClick={() => navigate("/login")}>
          Go to sign in
        </button>
      </AuthShell>
    );
  }

  if (answer === "no") {
    return (
      <AuthShell
        title="Your company needs registering first"
        subtitle="Someone has to set the company up before employees can join."
        onBack={() => setAnswer(null)}
      >
        <div className="flex justify-center mb-5 text-[#1C1A14]">
          <Building2 size={36} />
        </div>
        <p className="text-sm text-gray-700 mb-6">
          Ask the person who manages leave at your company to register it. Once they have,
          they can invite you by email. If that person is you, you can set it up now and
          you'll become the company admin.
        </p>
        <div className="space-y-3">
          <button type="button" className={primaryButtonClass} onClick={() => navigate("/signup")}>
            Set up my company
          </button>
          <button type="button" className={secondaryButtonClass} onClick={() => navigate("/login")}>
            Back to sign in
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Is your company registered?"
      subtitle="Does your company already use Clock-out?"
      onBack={() => navigate("/get-started")}
    >
      <div className="space-y-3">
        <button type="button" className={primaryButtonClass} onClick={() => setAnswer("yes")}>
          Yes, it is
        </button>
        <button type="button" className={secondaryButtonClass} onClick={() => setAnswer("no")}>
          No, or I'm not sure
        </button>
      </div>
    </AuthShell>
  );
};

export default JoinCompany;